import { DatePickerWithRange } from "components/ui/DatePickerWithRange";
import { format, getYear, isValid } from "date-fns";
import { Drawer } from "features/Drawer";
import { DrawerSettingsType } from "features/Drawer/types/types";
import Button from "hardcover-ui/components/Button";
import Icon from "hardcover-ui/components/Icon";
import Text from "hardcover-ui/components/Text";
import CalendarIcon from "icons/regular/calendar.svg";
import ChevronLeftIcon from "icons/regular/chevron-left.svg";
import ChevronRightIcon from "icons/regular/chevron-right.svg";
import { useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import { Period, useStatPeriod } from "./StatPeriodContext";

interface StatPeriodSelectProps {
  className?: string;
  buttonClassName?: string;
  textClassName?: string;
}

export default function StatPeriodSelect({
  className = "",
  buttonClassName = "",
  textClassName = "",
}: StatPeriodSelectProps) {
  const { selectedPeriod, customDateRange, updatePeriod, availableYears } =
    useStatPeriod();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"main" | "year" | "month" | "custom">(
    "main"
  );
  const [selectedYear, setSelectedYear] = useState(getYear(new Date()));
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const drawerSettings: DrawerSettingsType = {
    portalRef: document.body,
    ref: buttonRef.current,
    isOpen: true,
    onClose: () => setOpen(false),
    placement: "bottom",
  };

  const formatPeriodLabel = () => {
    switch (selectedPeriod) {
      case "life":
        return "Life";
      case "year":
        return format(customDateRange.from, "yyyy");
      case "month":
        return format(customDateRange.from, "MMM ''yy");
      case "custom":
        return `${format(customDateRange.from, "d MMM ''yy")} to ${format(
          customDateRange.to,
          "d MMM ''yy"
        )}`;
      default:
        return "Select Period";
    }
  };

  const handlePeriodChange = (period: Period) => {
    updatePeriod(period);
    setOpen(false);
  };

  const renderYearSelection = () => (
    <>
      <div className="grid grid-cols-3 gap-2">
        <Text className="col-span-3">Showing all available years</Text>
        {availableYears.map((year) => (
          <Button
            key={year}
            variant="tertiary"
            size="md"
            className="w-full justify-center"
            onClick={() => {
              updatePeriod("year", {
                from: new Date(year, 0, 1),
                to: new Date(year, 11, 31),
              });
              setOpen(false);
            }}
          >
            {year}
          </Button>
        ))}
      </div>
    </>
  );

  const renderMonthSelection = () => {
    const months = Array.from({ length: 12 }, (_, i) =>
      format(new Date(2000, i), "MMMM")
    );
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    return (
      <>
        <div className="flex justify-between items-center mb-4 bg-secondary/60 rounded-lg p-2">
          <Button
            size="md"
            variant="tertiary"
            onClick={() =>
              setSelectedYear((prev) => Math.max(prev - 1, availableYears[0]))
            }
            disabled={selectedYear <= availableYears[0]}
          >
            <Icon icon={ChevronLeftIcon} />
          </Button>
          <span className="text-lg font-bold">{selectedYear}</span>
          <Button
            size="md"
            variant="tertiary"
            onClick={() =>
              setSelectedYear((prev) => Math.min(prev + 1, currentYear))
            }
            disabled={selectedYear >= currentYear}
          >
            <Icon icon={ChevronRightIcon} />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {months.map((month, index) => (
            <Button
              key={month}
              variant="tertiary"
              className={`w-full justify-center ${
                selectedYear === currentYear && index > currentMonth
                  ? "opacity-20"
                  : ""
              }`}
              size="md"
              onClick={() => {
                updatePeriod("month", {
                  from: new Date(selectedYear, index, 1),
                  to: new Date(selectedYear, index + 1, 0),
                });
                setOpen(false);
              }}
              disabled={selectedYear === currentYear && index > currentMonth}
            >
              {month}
            </Button>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <div ref={portalRef} className={className}>
        <Button
          ref={buttonRef}
          size="sm"
          className={`${buttonClassName} rounded-md hover:opacity-70 transition-all`}
          onClick={() => setOpen(true)}
        >
          <Icon
            icon={CalendarIcon}
            className="h-4 w-4 inline-block"
            aria-hidden="true"
          />
          <span className={textClassName}>{formatPeriodLabel()}</span>
        </Button>
      </div>

      {open ? (
        <Drawer settings={drawerSettings}>
          <div className="p-3 w-[320px] mx-auto">
            {view !== "main" && (
              <Button
                size="sm"
                variant="tertiary"
                className="mb-2"
                onClick={() => setView("main")}
              >
                <Icon size="xs" icon={ChevronLeftIcon} />
                Back
              </Button>
            )}

            {view === "main" && (
              <div className="flex flex-col gap-1">
                <Button
                  variant="tertiary"
                  size="lg"
                  className="w-full"
                  onClick={() => handlePeriodChange("life")}
                >
                  All time
                </Button>
                <Button
                  variant="tertiary"
                  size="lg"
                  className="w-full"
                  onClick={() => setView("year")}
                >
                  By Year
                </Button>
                <Button
                  variant="tertiary"
                  size="lg"
                  className="w-full"
                  onClick={() => setView("month")}
                >
                  By Month
                </Button>
                <Button
                  variant="tertiary"
                  size="lg"
                  className="w-full"
                  onClick={() => setView("custom")}
                >
                  Custom
                </Button>
              </div>
            )}

            {view === "year" && renderYearSelection()}
            {view === "month" && renderMonthSelection()}

            {view === "custom" && (
              <div className="flex flex-col gap-2">
                <DatePickerWithRange
                  startDate={customDateRange.from}
                  endDate={customDateRange.to}
                  onChange={(range: DateRange | undefined) => {
                    if (
                      range &&
                      range.from &&
                      range.to &&
                      isValid(range.from) &&
                      isValid(range.to)
                    ) {
                      updatePeriod("custom", {
                        from: range.from,
                        to: range.to,
                      });
                      setOpen(false);
                    }
                  }}
                />
                <div className="flex justify-end gap-2 mt-2">
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setView("main")}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setOpen(false)}>
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Drawer>
      ) : (
        false
      )}
    </>
  );
}
